import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { MODULES } from "@/config/modules";
import ResourceForm from "@/components/ResourceForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const module = MODULES.emergency;

const CATEGORY_META = {
  Hospital: { icon: "Cross", color: "#EF4444" },
  Ambulance: { icon: "Ambulance", color: "#F43F5E" },
  Police: { icon: "Shield", color: "#3B82F6" },
  "Fire Brigade": { icon: "Flame", color: "#F97316" },
  "Site Security": { icon: "UserCheck", color: "#10B981" },
  "Client Emergency Contact": { icon: "Building2", color: "#8B5CF6" },
  "Electrical Emergency": { icon: "Zap", color: "#EAB308" },
  "Machine Technician": { icon: "Wrench", color: "#0EA5E9" },
  "Insurance Contact": { icon: "FileShield", color: "#14B8A6" },
  Management: { icon: "Briefcase", color: "#6366F1" },
  Other: { icon: "Phone", color: "#64748B" },
};

export default function Emergency() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    if (!can("emergency", "view")) { setLoading(false); return; }
    setLoading(true);
    const params = q ? { q } : {};
    api.get("/resource/emergency", { params })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/resource/emergency/${deleteId}`);
      toast.success("Contact deleted"); setDeleteId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const telLink = (v) => `tel:${String(v).replace(/\s/g, "")}`;

  if (!can("emergency", "view"))
    return (
      <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
        <div className="h-14 w-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-4">
          <Icons.Lock className="h-6 w-6" />
        </div>
        <h2 className="font-display text-xl font-bold">Access Restricted</h2>
        <p className="text-muted-foreground text-sm mt-2">You don't have permission to view Emergency contacts.</p>
      </div>
    );

  const grouped = module.filters.category.reduce((acc, cat) => {
    const list = items.filter((i) => (i.category || "Other") === cat);
    if (list.length) acc.push([cat, list]);
    return acc;
  }, []);
  const ungrouped = items.filter((i) => !module.filters.category.includes(i.category || "Other"));
  if (ungrouped.length) grouped.push(["Other", ungrouped]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400">
            <Icons.Siren className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Emergency Information</h1>
            <p className="text-sm text-muted-foreground">Tap any number to call instantly.</p>
          </div>
        </div>
        <Button className="gap-1.5 active:scale-95 transition-transform" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-record-btn" disabled={!can("emergency", "create")}>
          <Icons.Plus className="h-4 w-4" />New Contact
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search emergency contacts…" className="pl-9" data-testid="module-search" />
      </div>

      {loading && <p className="text-muted-foreground text-sm">Loading…</p>}
      {!loading && items.length === 0 && (
        <Card className="p-12 text-center">
          <Icons.Siren className="h-8 w-8 mx-auto opacity-40 mb-3 text-red-500" />
          <p className="text-sm text-muted-foreground">No emergency contacts yet.</p>
          <Button size="sm" variant="secondary" className="mt-3" onClick={() => { setEditing(null); setFormOpen(true); }}>Add the first contact</Button>
        </Card>
      )}

      <div className="space-y-7">
        {grouped.map(([cat, list]) => {
          const meta = CATEGORY_META[cat] || CATEGORY_META.Other;
          const CatIcon = Icons[meta.icon] || Icons.Phone;
          return (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
                  <CatIcon className="h-4 w-4" />
                </div>
                <h3 className="font-display font-semibold">{cat}</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((c, i) => (
                  <motion.div key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                    className="p-4 rounded-xl bg-card border border-border hover:shadow-md transition-shadow" data-testid={`emergency-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{c.name}</div>
                        {c.organization && <div className="text-sm text-muted-foreground truncate">{c.organization}</div>}
                        {c.available_hours && <div className="text-xs text-muted-foreground mt-0.5">{c.available_hours}</div>}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" data-testid={`row-menu-${c.id}`}><Icons.MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setEditing(c); setFormOpen(true); }} data-testid={`edit-${c.id}`}><Icons.Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => setDeleteId(c.id)} data-testid={`delete-${c.id}`}><Icons.Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {c.phone && (
                        <a href={telLink(c.phone)} data-testid={`call-${c.id}`}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium active:scale-95 transition-transform hover:bg-emerald-700">
                          <Icons.Phone className="h-4 w-4" />{c.phone}
                        </a>
                      )}
                      {c.alt_phone && (
                        <a href={telLink(c.alt_phone)} data-testid={`call-alt-${c.id}`}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary text-sm font-medium hover:bg-accent transition-colors">
                          <Icons.PhoneCall className="h-4 w-4" />Alt
                        </a>
                      )}
                      {c.map_link && (
                        <a href={c.map_link} target="_blank" rel="noreferrer" data-testid={`map-${c.id}`}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary text-sm font-medium hover:bg-accent transition-colors">
                          <Icons.MapPin className="h-4 w-4" />Map
                        </a>
                      )}
                    </div>
                    {c.address && <p className="text-xs text-muted-foreground mt-2">{c.address}</p>}
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} module={module} record={editing} onSaved={load} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
            <AlertDialogDescription>This emergency contact will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
